import CloseIcon from "@mui/icons-material/Close";
import { Alert, Collapse, IconButton, Stack } from "@mui/material";
import ErrorIcon from "assets/images/warning-2.svg";
import { CircularLoaderFullPage } from "components/core";
import DownloadPopup from "components/core/modal-components/DownloadPopup";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { CF_URLS } from "services/api/endpoints";
import { selectAuth } from "store/slices";
import spacing from "styles/spacing";
import {
  downloadOptionPdf,
  ERROR_TYPES,
  MEDIA_PROGRAM_TYPES,
  VALUE_EMPTY_STRING
} from "utils/constants";
import {
  reportLable,
  severityError
} from "utils/constants/discrepancy-management/DiscrepancyDetails";
import {
  dataDogErrorLog,
  findErrorMessage,
  getUserName,
  handleRefId,
  printReport
} from "utils/helpers";
import AuthForm from "../discrepancy-update/AuthForm";
import { DetailsStyles } from "./DetailsStyles";
import DiscrepancyDetails from "./DiscrepancyDetails";

const DiscrepancyManagementDetailLayout = () => {
  const { showAuthSection, isAuthenticated } = useSelector(
    (state) => state.discrepancyUpdate
  );
  const { currentBranch, auth } = useSelector(selectAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [throwErrorMessage, setThrowErrorMessage] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [reportCUrl, setReportCFUrl] = useState(VALUE_EMPTY_STRING);
  const [selectedValue, setSelectedValue] = useState(downloadOptionPdf);

  let location = useLocation();
  let typeOfMedia = location?.state?.type;

  useEffect(() => {
    if (throwErrorMessage) {
      throwErrorMessage && window.scrollTo(0, 0);
    }

    //eslint-disable-next-line
  }, [throwErrorMessage]);

  const handlePrint = async () => {
    try {
      setIsLoading(true);
      const requestBody = {
        main_district_id: currentBranch?.value,
        district_id: printData?.district_id ? printData?.district_id : "",
        om_discrepancy_id: printData?.om_discrepancy_id
          ? printData?.om_discrepancy_id
          : "",
        media_type: printData?.media_type ? printData?.media_type : "",
        show_additional_system_comments:
          printData?.show_additional_system_comments
            ? printData?.show_additional_system_comments
            : "",
        show_bypass_reason: printData?.show_bypass_reason
          ? printData?.show_bypass_reason
          : "",
        user_name: getUserName(auth?.user),
        report_format: selectedValue?.type?.toLocaleUpperCase(),
        detected_in: printData?.detected_in ? printData?.detected_in : "",
        resolution: printData?.resolution ? printData?.resolution : "",
        location: printData?.location ? printData?.location : "",
        run: printData?.run ? printData?.run : "",
        run_status: printData?.run_status ? printData?.run_status : "",
        pending_return_date: printData?.pending_return_date
          ? printData?.pending_return_date
          : "",
        service_date: printData?.service_date ? printData?.service_date : "",
        cstomer_media_identifier: printData?.cstomer_media_identifier
          ? printData?.cstomer_media_identifier
          : "",
        ...reportLable
      };
      let url = CF_URLS.printReports.discrepancy.discrepancyDetailReport;
      if (typeOfMedia === MEDIA_PROGRAM_TYPES.CONTAINER.TYPE) {
        delete requestBody.om_discrepancy_id;
        requestBody.discrepancy_id = printData?.om_discrepancy_id ?? null;
        url = CF_URLS.printReports.discrepancy.containerDiscrepancyDetailReport;
      }

      await printReport(JSON.stringify(requestBody), url);
      setShowPrintModal(false);
      setIsLoading(false);
    } catch (e) {
      const { refId, message } = handleRefId(
        findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(
        message,
        CF_URLS.printReports.discrepancy.discrepancyDetailReport,
        { error: e },
        refId
      );
      setThrowErrorMessage(message);
      setIsLoading(false);
      setShowPrintModal(false);
    }
  };

  return (
    <Stack gap={spacing.gap}>
      <CircularLoaderFullPage loading={isLoading} />
      <AuthForm setLoading={setIsLoading} />
      {throwErrorMessage && (
        <Collapse in={!!throwErrorMessage}>
          <Alert
            severity={severityError}
            sx={DetailsStyles.errorStyle}
            icon={
              <img
                src={ErrorIcon}
                alt="error"
                style={DetailsStyles.iconStyle}
              />
            }
            action={
              <IconButton
                aria-label={t("common.close")}
                color="inherit"
                size="small"
                onClick={() => {
                  setThrowErrorMessage(null);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {throwErrorMessage}
          </Alert>
        </Collapse>
      )}
      <DownloadPopup
        title={t("common.downloadReport")}
        showPrintModal={showPrintModal}
        onCancleClick={() => {
          setShowPrintModal(false);
          setPrintData(null);
          setReportCFUrl(VALUE_EMPTY_STRING);
        }}
        onDownloadClick={() => {
          handlePrint();
        }}
        cfUrl={reportCUrl}
        setSelectedValue={setSelectedValue}
      />
      {(!showAuthSection || isAuthenticated) && (
        <DiscrepancyDetails
          setIsLoading={setIsLoading}
          setThrowErrorMessage={setThrowErrorMessage}
          setShowPrintModal={(value) => {
            setPrintData(value);
            setShowPrintModal(true);
            setReportCFUrl(
              CF_URLS.printReports.discrepancy.discrepancyDetailReport
            );
          }}
          isLoading={isLoading}
          isAuthenticated={isAuthenticated}
        />
      )}
    </Stack>
  );
};

export default DiscrepancyManagementDetailLayout;
